import clsx from 'clsx';
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CaremoryMark } from '@/components/icons/caremory-mark';
import {
  loadSharedProfile,
  type LoadShareOutcome,
  type PublicShareData,
  type PublicShareEvent,
} from '@/features/share/lib/shareApi';
import { AiSummaryCard } from '@/features/share/components/ai-summary-card';
import { formatLongDate } from '@/features/timeline/lib/formatDate';
import { formatDoctorName } from '@/features/timeline/lib/formatDoctor';
import { getEventTypeMeta } from '@/features/timeline/lib/eventType';
import { PhotoLightbox } from '@/features/timeline/components/photo-lightbox';

const DISCLAIMER =
  'This app stores records. It does not provide medical advice. For clinical decisions, consult a licensed clinician.';

const FALLBACK_SUMMARY = 'Insufficient records to summarize.';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: PublicShareData }
  | { kind: 'expired' }
  | { kind: 'not_found' }
  | { kind: 'password_required'; wrongPassword: boolean }
  | { kind: 'error' };

type Tab = 'overview' | 'timeline' | 'medications';

function calculateAge(dob: string | null): { years: number; months: number } | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  let months = now.getMonth() - d.getMonth();
  if (now.getDate() < d.getDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  return { years, months };
}

function formatAge(age: { years: number; months: number }): string {
  if (age.years === 0) return `${age.months} month${age.months === 1 ? '' : 's'} old`;
  if (age.months === 0) return `${age.years} year${age.years === 1 ? '' : 's'} old`;
  return `${age.years} yr ${age.months} mo old`;
}

export function SharePage() {
  const { token } = useParams<{ token: string; name?: string }>();
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [focusedEventId, setFocusedEventId] = useState<string | null>(null);

  const load = useCallback(async (password?: string) => {
    if (!token) {
      setState({ kind: 'not_found' });
      return;
    }
    if (!password) setState({ kind: 'loading' });
    const outcome: LoadShareOutcome = await loadSharedProfile(token, password);
    if (outcome.status === 'ready') {
      setState({ kind: 'ready', data: outcome.data });
      return;
    }
    if (outcome.status === 'expired') { setState({ kind: 'expired' }); return; }
    if (outcome.status === 'not_found') { setState({ kind: 'not_found' }); return; }
    if (outcome.status === 'password_required') {
      setState({ kind: 'password_required', wrongPassword: false });
      return;
    }
    if (outcome.status === 'wrong_password') {
      setState({ kind: 'password_required', wrongPassword: true });
      return;
    }
    setState({ kind: 'error' });
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.kind === 'loading') {
    return (
      <main className="min-h-screen bg-canvas text-ink flex items-center justify-center px-4">
        <p className="text-ink-muted text-sm" role="status">
          Loading record…
        </p>
      </main>
    );
  }

  if (state.kind === 'password_required') {
    return (
      <PasswordGate
        wrongPassword={state.wrongPassword}
        onSubmit={(pw) => load(pw)}
      />
    );
  }

  if (state.kind === 'expired') {
    return <ShareMessage title="This link has expired" body="Ask the family member to generate a new link." />;
  }

  if (state.kind === 'not_found') {
    return <ShareMessage title="Link not found" body="This share link doesn't exist or has been revoked." />;
  }

  if (state.kind === 'error') {
    return (
      <main className="min-h-screen bg-canvas text-ink flex items-center justify-center px-4">
        <div className="text-center max-w-share w-full">
          <p className="font-medium mb-2">We couldn't load this record.</p>
          <button
            type="button"
            onClick={() => load()}
            className="min-h-tap min-w-tap inline-flex items-center justify-center px-4 text-sm underline text-accent"
          >
            Try again
          </button>
        </div>
      </main>
    );
  }

  return (
    <ShareView
      data={state.data}
      tab={tab}
      setTab={setTab}
      focusedEventId={focusedEventId}
      setFocusedEventId={setFocusedEventId}
      lightboxUrl={lightboxUrl}
      setLightboxUrl={setLightboxUrl}
    />
  );
}

function ShareView({
  data,
  tab,
  setTab,
  focusedEventId,
  setFocusedEventId,
  lightboxUrl,
  setLightboxUrl,
}: {
  data: PublicShareData;
  tab: Tab;
  setTab: (t: Tab) => void;
  focusedEventId: string | null;
  setFocusedEventId: (id: string | null) => void;
  lightboxUrl: string | null;
  setLightboxUrl: (s: string | null) => void;
}) {
  const { profile, summary, summary_created_at, expires_at, events } = data;
  const firstName = profile.name.split(' ')[0];
  const age = calculateAge(profile.date_of_birth);
  const activeMeds = useMemo(() => collectActiveMeds(events), [events]);
  const dateRange = useMemo(() => computeDateRange(events), [events]);
  const typeCounts = useMemo(() => countByType(events), [events]);
  const summaryText = summary?.trim() ?? '';
  const hasSummary = summaryText.length > 0 && summaryText !== FALLBACK_SUMMARY;

  const eventIdSet = useMemo(() => new Set(events.map((e) => e.id)), [events]);
  const handleCitationClick = useCallback(
    (eventId: string) => {
      if (!eventIdSet.has(eventId)) return;
      setTab('timeline');
      setFocusedEventId(eventId);
    },
    [eventIdSet, setTab, setFocusedEventId],
  );

  return (
    <main className="min-h-screen bg-canvas text-ink print:bg-white anim-fade-in">
      <div className="max-w-share mx-auto px-4 sm:px-6 pt-6 pb-10 print:px-0 print:pt-0 print:pb-0">
        {/* ---------- Brand bar ---------- */}
        <div className="flex items-center justify-between gap-3 mb-5 print:mb-4">
          <BrandMark />
          <button
            type="button"
            onClick={() => window.print()}
            className="print:hidden inline-flex items-center gap-2 min-h-tap px-3.5 rounded-lg bg-accent text-white text-sm font-medium shadow-card hover:bg-accent-hover shrink-0"
            aria-label="Print this record"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </button>
        </div>

        {/* ---------- Hero ---------- */}
        <header className="mb-5 print:mb-4">
          <p className="text-[11px] uppercase tracking-[0.14em] text-ink-muted font-semibold mb-1">
            Patient record
          </p>
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-3xl sm:text-[32px] font-bold leading-tight tracking-tight">
              {profile.name}
            </h1>
            {dateRange && (
              <div
                title="History date range"
                aria-label={`History date range: ${dateRange}`}
                className="flex items-center gap-1.5 mt-1.5 shrink-0 text-sm text-ink-muted print:text-black"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4 shrink-0 text-accent"
                  aria-hidden="true"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                <span className="tabular-nums">{dateRange}</span>
              </div>
            )}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[15px] text-ink-muted">
            {age !== null ? <span>{formatAge(age)}</span> : <span>Age not recorded</span>}
          </div>
        </header>

        {/* ---------- Patient / health info (only filled fields, allergies included) ---------- */}
        <PatientInfo profile={profile} />

        {/* ---------- Record types (above tabs, always visible) ---------- */}
        {Object.keys(typeCounts).length > 0 && (
          <div className="mb-5">
            <p className="text-[11px] uppercase tracking-[0.12em] text-ink-muted font-semibold mb-2">
              What's in this record
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(typeCounts).map(([type, count]) => {
                const meta = getEventTypeMeta(type);
                return (
                  <span
                    key={type}
                    className={clsx(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold',
                      meta.pillBg,
                      meta.pillText,
                    )}
                  >
                    <meta.Icon className="h-3.5 w-3.5" />
                    {meta.label} · {count}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* ---------- Tabs (screen only) ---------- */}
        <nav
          className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-canvas/95 backdrop-blur border-b border-border mb-5 print:hidden"
          aria-label="Record sections"
        >
          <div role="tablist" className="flex gap-1 bg-white rounded-lg p-1 border border-border shadow-card">
            <TabButton active={tab === 'overview'} onClick={() => setTab('overview')} label="Overview" />
            <TabButton active={tab === 'timeline'} onClick={() => setTab('timeline')} label={`Timeline (${events.length})`} />
            <TabButton active={tab === 'medications'} onClick={() => setTab('medications')} label={`Meds (${activeMeds.length})`} />
          </div>
        </nav>

        {/* ---------- Overview tab ---------- */}
        <TabPanel visible={tab === 'overview'} label="Overview">
          <SectionHeader
            eyebrow="At a glance"
            title={hasSummary ? `AI Summary for ${firstName}` : 'AI Summary'}
          />
          {hasSummary ? (
            <AiSummaryCard
              text={summaryText}
              recordCount={events.length}
              createdAt={summary_created_at}
              onCitationClick={handleCitationClick}
            />
          ) : (
            <div className="card px-5 py-5 print:shadow-none print:border-ink/20">
              <p className="text-sm text-ink-muted italic">{FALLBACK_SUMMARY}</p>
            </div>
          )}

          {activeMeds.length > 0 && (
            <>
              <SectionHeader
                eyebrow="Currently prescribed"
                title="Active medications"
                count={activeMeds.length}
                topGap
              />
              <ul className="card divide-y divide-border print:shadow-none print:border-ink/20 print:break-inside-avoid">
                {activeMeds.slice(0, 6).map((m, idx) => (
                  <li key={idx} className="px-5 py-3">
                    <MedicationLine med={m} />
                  </li>
                ))}
                {activeMeds.length > 6 && (
                  <li className="px-5 py-3 text-xs text-ink-muted">
                    + {activeMeds.length - 6} more — see the Meds tab
                  </li>
                )}
              </ul>
            </>
          )}
        </TabPanel>

        {/* ---------- Timeline tab ---------- */}
        <TabPanel visible={tab === 'timeline'} label="Timeline">
          <SectionHeader
            eyebrow="Chronological history"
            title="Timeline"
            count={events.length}
          />
          {events.length === 0 ? (
            <div className="card px-5 py-5 print:shadow-none print:border-ink/20">
              <p className="text-sm text-ink-muted">No records on file.</p>
            </div>
          ) : (
            <TimelineByYear
              events={events}
              onOpenPhoto={setLightboxUrl}
              focusedEventId={focusedEventId}
              onFocusCleared={() => setFocusedEventId(null)}
            />
          )}
        </TabPanel>

        {/* ---------- Medications tab ---------- */}
        <TabPanel visible={tab === 'medications'} label="Medications">
          <SectionHeader
            eyebrow="Currently prescribed"
            title="Active medications"
            count={activeMeds.length}
          />
          {activeMeds.length === 0 ? (
            <div className="card px-5 py-5 print:shadow-none print:border-ink/20">
              <p className="text-sm text-ink-muted">
                No currently active medications on file.
              </p>
            </div>
          ) : (
            <ul className="card divide-y divide-border print:shadow-none print:border-ink/20">
              {activeMeds.map((m, idx) => (
                <li key={idx} className="px-5 py-3 print:break-inside-avoid">
                  <MedicationLine med={m} showDates />
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-ink-muted">
            "Active" = medication with a future end date, or a recent prescription
            within the last 60 days. Full prescribing history is in the Timeline tab.
          </p>
        </TabPanel>

        {/* ---------- Footer ---------- */}
        <footer className="mt-10 pt-5 border-t border-border text-xs text-ink-muted space-y-2 print:mt-6">
          <p>
            Summary generated by Claude Opus 4.7 from {events.length}{' '}
            {events.length === 1 ? 'record' : 'records'}. Originals attached in the
            timeline.
          </p>
          <p>
            Link expires {formatLongDate(expires_at.slice(0, 10))}. Attachments use
            5-minute signed URLs; refresh the page to renew.
          </p>
          <p className="italic text-ink print:text-black pt-2 border-t border-border">
            {DISCLAIMER}
          </p>
        </footer>
      </div>

      {lightboxUrl && (
        <div className="print:hidden">
          <PhotoLightbox
            src={lightboxUrl}
            alt="Original record photo"
            onClose={() => setLightboxUrl(null)}
          />
        </div>
      )}
    </main>
  );
}

/* ---------- Small UI atoms ------------------------------------------- */

function BrandMark() {
  return (
    <div className="flex flex-col gap-0.5">
      <CaremoryMark variant="primary" className="h-8 print:h-7" title="Caremory" />
      <p className="text-xs font-semibold text-ink-muted tracking-wide print:text-black">Care that remembers</p>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={clsx(
        'flex-1 min-h-tap px-3 rounded-md text-sm transition-colors',
        active
          ? 'bg-accent text-white shadow-card font-bold'
          : 'text-ink-muted hover:text-ink hover:bg-canvas font-semibold',
      )}
    >
      {label}
    </button>
  );
}

function TabPanel({
  visible,
  label,
  children,
}: {
  visible: boolean;
  label: string;
  children: React.ReactNode;
}) {
  // Screen: only show the active panel. Print: always show (stacked).
  return (
    <section
      aria-label={label}
      className={clsx(
        'space-y-3 print:break-before-auto',
        !visible && 'hidden print:block',
        visible && 'print:mt-6',
      )}
    >
      {children}
    </section>
  );
}

function SectionHeader({
  eyebrow,
  title,
  count,
  topGap = false,
}: {
  eyebrow: string;
  title: string;
  count?: number;
  topGap?: boolean;
}) {
  return (
    <div className={clsx(topGap && 'pt-4')}>
      <p className="text-[11px] uppercase tracking-[0.12em] text-ink-muted font-semibold mb-1">
        {eyebrow}
      </p>
      <h2 className="text-lg font-bold text-ink mb-3">
        {title}
        {typeof count === 'number' && (
          <span className="ml-2 text-sm font-normal text-ink-muted">{count}</span>
        )}
      </h2>
    </div>
  );
}

function PatientInfo({ profile }: { profile: PublicShareData['profile'] }) {
  const rows: Array<{ label: string; value: string }> = [];

  if (profile.date_of_birth) {
    rows.push({ label: 'Born', value: formatLongDate(profile.date_of_birth) });
  }
  if (profile.gender) {
    rows.push({
      label: 'Sex',
      value:
        profile.gender === 'male'
          ? 'Male'
          : profile.gender === 'female'
          ? 'Female'
          : 'Other',
    });
  }
  if (profile.blood_type) {
    rows.push({ label: 'Blood type', value: profile.blood_type });
  }
  if (profile.height_cm != null) {
    rows.push({ label: 'Height', value: `${profile.height_cm} cm` });
  }
  if (profile.weight_kg != null) {
    rows.push({ label: 'Weight', value: `${profile.weight_kg} kg` });
  }
  const bmi = calculateBMI(profile.weight_kg, profile.height_cm);
  if (bmi !== null) {
    rows.push({ label: 'BMI', value: bmi });
  }

  const hasAllergies = profile.allergies.length > 0;
  const hasRows = rows.length > 0;

  // Hide the entire card when there is nothing to show at all.
  if (!hasRows && !hasAllergies) return null;

  return (
    <section className="mb-5 card overflow-hidden print:shadow-none print:border-ink/20 print:break-inside-avoid">
      {/* Section header */}
      <div className="px-4 pt-3.5 pb-3 flex items-center gap-2 border-b border-border">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 text-accent shrink-0"
          aria-hidden="true"
        >
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21a8 8 0 0 1 16 0" />
        </svg>
        <h2 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-muted">
          Patient info &amp; health details
        </h2>
      </div>

      {/* Allergies row — only when filled, rendered with warning styling */}
      {hasAllergies && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border-b border-flag-block/20 print:bg-white">
          <span
            aria-hidden="true"
            className="shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full bg-flag-block text-white text-xs font-bold print:border print:border-flag-block print:bg-white print:text-flag-block"
          >
            !
          </span>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-flag-block">
              Allergies
            </p>
            <p className="text-[15px] font-semibold text-ink leading-snug">
              {profile.allergies.join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Key-value grid for all other profile/health fields */}
      {hasRows && (
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0 px-4 py-1">
          {rows.map((row, idx) => (
            <div
              key={row.label}
              className={clsx(
                'py-2.5',
                idx < rows.length - (rows.length % 2 === 0 ? 2 : 1)
                  ? 'border-b border-border'
                  : '',
              )}
            >
              <dt className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold">
                {row.label}
              </dt>
              <dd className="text-[15px] text-ink font-semibold mt-0.5 leading-snug">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

function calculateBMI(
  weight_kg: number | null | undefined,
  height_cm: number | null | undefined,
): string | null {
  if (weight_kg == null || height_cm == null || height_cm === 0) return null;
  const bmi = weight_kg / (height_cm / 100) ** 2;
  return bmi.toFixed(1);
}

// AiSummaryCard is defined in src/features/share/components/ai-summary-card.tsx
// and imported above. The share page wraps it in print-friendly classes below.

function MedicationLine({
  med,
  showDates = false,
}: {
  med: PublicShareEvent['medications'][number];
  showDates?: boolean;
}) {
  return (
    <div>
      <p className="text-[15px] font-semibold text-ink leading-snug">
        {med.drug_name ?? 'Unnamed drug'}
      </p>
      <p className="text-sm text-ink-muted mt-0.5">
        {[med.dosage, med.frequency].filter(Boolean).join(' · ') || 'No dose recorded'}
      </p>
      {showDates && (med.start_date || med.end_date || med.duration_days != null) && (
        <p className="text-xs text-ink-muted mt-1">
          {med.start_date && `Started ${formatLongDate(med.start_date)}`}
          {med.end_date && ` · ends ${formatLongDate(med.end_date)}`}
          {!med.end_date && med.duration_days != null && ` · ${med.duration_days} day${med.duration_days === 1 ? '' : 's'}`}
        </p>
      )}
    </div>
  );
}

/* ---------- Timeline grouped by year --------------------------------- */

function TimelineByYear({
  events,
  onOpenPhoto,
  focusedEventId,
  onFocusCleared,
}: {
  events: PublicShareEvent[];
  onOpenPhoto: (url: string) => void;
  focusedEventId: string | null;
  onFocusCleared: () => void;
}) {
  const groups = useMemo(() => groupByYear(events), [events]);

  return (
    <div className="space-y-6">
      {groups.map(([year, yearEvents]) => (
        <div key={year} className="print:break-inside-avoid-page">
          <div className="flex items-center gap-3 mb-3">
            <h3 className="text-sm font-semibold text-ink tabular-nums">{year}</h3>
            <div className="flex-1 h-px bg-border" aria-hidden="true" />
            <span className="text-xs text-ink-muted tabular-nums">
              {yearEvents.length} {yearEvents.length === 1 ? 'record' : 'records'}
            </span>
          </div>
          <ol className="space-y-3">
            {yearEvents.map((event) => (
              <li key={event.id}>
                <ShareEventCard
                  event={event}
                  onOpenPhoto={onOpenPhoto}
                  isFocused={focusedEventId === event.id}
                  onFocusCleared={onFocusCleared}
                />
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

function ShareEventCard({
  event,
  onOpenPhoto,
  isFocused,
  onFocusCleared,
}: {
  event: PublicShareEvent;
  onOpenPhoto: (url: string) => void;
  isFocused: boolean;
  onFocusCleared: () => void;
}) {
  const meta = getEventTypeMeta(event.event_type);
  const headline = event.diagnosis?.trim() || event.title?.trim() || null;
  const photoAttachment = event.attachments.find(
    (a) => a.signed_url && (a.file_type?.startsWith('image/') ?? false),
  );
  const pdfAttachment = event.attachments.find(
    (a) => a.signed_url && a.file_type === 'application/pdf',
  );

  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isFocused) return;
    const node = ref.current;
    if (node) {
      // Wait a tick so the tab swap has rendered the card before we scroll.
      const t = window.setTimeout(() => {
        node.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 60);
      const clear = window.setTimeout(onFocusCleared, 2500);
      return () => {
        window.clearTimeout(t);
        window.clearTimeout(clear);
      };
    }
  }, [isFocused, onFocusCleared]);

  return (
    <article
      ref={ref}
      data-event-id={event.id}
      className={clsx(
        'card p-4 transition-shadow duration-300 scroll-mt-20',
        isFocused && 'ring-2 ring-accent shadow-lg',
        'print:shadow-none print:break-inside-avoid',
      )}
    >
      <div className="min-w-0">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex items-start gap-2.5">
            <span
              className={clsx(
                'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                meta.iconBg,
                meta.iconText,
              )}
              aria-hidden="true"
            >
              <meta.Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              {headline && (
                <p className="text-[15px] font-bold text-ink leading-snug">
                  {headline}
                </p>
              )}
              <p className="text-xs text-ink-muted mt-0.5">
                <span
                  className={clsx(
                    'inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold mr-1.5',
                    meta.pillBg,
                    meta.pillText,
                  )}
                >
                  {meta.label}
                </span>
                {event.doctor_name && <>{formatDoctorName(event.doctor_name)}</>}
                {event.doctor_name && event.clinic_name && ' · '}
                {event.clinic_name}
              </p>
            </div>
          </div>
          <p className="text-xs text-ink-muted shrink-0 tabular-nums whitespace-nowrap">
            {formatLongDate(event.event_date)}
          </p>
        </div>

        {event.medications.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {event.medications.map((m, idx) => (
              <li
                key={idx}
                className="text-sm rounded-md bg-canvas px-3 py-2 print:bg-white print:border print:border-border"
              >
                <span className="font-semibold text-ink">
                  {m.drug_name ?? 'Unnamed drug'}
                </span>
                {m.dosage && <span className="text-ink-muted"> · {m.dosage}</span>}
                {m.frequency && <span className="text-ink-muted"> · {m.frequency}</span>}
                {m.duration_days != null && (
                  <span className="text-ink-muted">
                    {' '}· {m.duration_days} day{m.duration_days === 1 ? '' : 's'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {event.notes && (
          <p className="mt-2 text-sm text-ink leading-relaxed">{event.notes}</p>
        )}

        {event.ai_flags.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-1.5" aria-label="AI extraction flags">
            {event.ai_flags.map((flag, idx) => (
              <li
                key={idx}
                className={clsx(
                  'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium print:bg-white',
                  flag.severity === 'block'
                    ? 'bg-red-50 text-flag-block border-flag-block/40'
                    : flag.severity === 'warn'
                    ? 'bg-amber-50 text-flag-warn border-flag-warn/30'
                    : 'bg-blue-50 text-flag-info border-flag-info/20',
                )}
              >
                {flag.message}
              </li>
            ))}
          </ul>
        )}

        {photoAttachment && photoAttachment.signed_url && (
          <button
            type="button"
            onClick={() => onOpenPhoto(photoAttachment.signed_url as string)}
            className="mt-3 inline-flex items-center gap-1 min-h-tap text-xs font-medium text-accent underline print:hidden"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-3.5 w-3.5"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="9" cy="9" r="2" />
              <path d="m21 15-5-5L5 21" />
            </svg>
            View original photo
          </button>
        )}

        {pdfAttachment && pdfAttachment.signed_url && (
          <a
            href={pdfAttachment.signed_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 min-h-tap text-xs font-medium text-accent underline print:hidden"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-3.5 w-3.5"
              aria-hidden="true"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            View original PDF
          </a>
        )}
      </div>
    </article>
  );
}

/* ---------- Message screen ------------------------------------------- */

function ShareMessage({ title, body }: { title: string; body: string }) {
  return (
    <main className="min-h-screen bg-canvas text-ink flex items-center justify-center px-4">
      <div className="text-center max-w-share w-full">
        <h1 className="text-xl font-bold mb-2">{title}</h1>
        <p className="text-sm text-ink-muted">{body}</p>
      </div>
    </main>
  );
}

function PasswordGate({
  wrongPassword,
  onSubmit,
}: {
  wrongPassword: boolean;
  onSubmit: (password: string) => void;
}) {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    setSubmitting(true);
    await onSubmit(value.trim());
    setSubmitting(false);
  }

  return (
    <main className="min-h-screen bg-canvas text-ink flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-surface rounded-card shadow-card px-6 py-7">
          <div className="mb-5 flex flex-col items-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-ink-muted" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <h1 className="text-lg font-bold">Password required</h1>
            <p className="text-sm text-ink-muted text-center">This record is password protected. Enter the password to view.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter password"
                className="input w-full pr-10"
                autoFocus
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 min-w-tap min-h-tap flex items-center justify-center text-ink-muted"
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {wrongPassword && (
              <p className="text-sm text-flag-block" role="alert">
                Incorrect password. Please try again.
              </p>
            )}
            <button
              type="submit"
              disabled={submitting || !value.trim()}
              className="btn-primary w-full"
            >
              {submitting ? 'Checking…' : 'View record'}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}

/* ---------- Helpers --------------------------------------------------- */


function medIsActive(
  m: PublicShareEvent['medications'][number],
  today: string,
  eventDate?: string | null,
): boolean {
  if (m.end_date) return m.end_date >= today;
  // Fall back to event date when medication start_date is null (older records)
  const effectiveStart = m.start_date ?? eventDate ?? null;
  if (effectiveStart && m.duration_days != null) {
    const start = new Date(effectiveStart);
    if (Number.isNaN(start.getTime())) return false;
    const finished = new Date(start);
    finished.setDate(finished.getDate() + m.duration_days);
    return finished.toISOString().slice(0, 10) >= today;
  }
  // Start date present but no duration → treat as ongoing/chronic
  if (effectiveStart) return true;
  // No date anchors at all → assume ongoing (matches profile page behaviour)
  return true;
}

function collectActiveMeds(events: PublicShareEvent[]) {
  const today = new Date().toISOString().slice(0, 10);
  const seen = new Set<string>();
  const result: PublicShareEvent['medications'] = [];
  for (const e of events) {
    for (const m of e.medications) {
      if (!m.drug_name) continue;
      if (!medIsActive(m, today, e.event_date)) continue;
      // Dedupe by drug + dosage so titrations and same-drug-different-dose
      // duplicates are both surfaced to the clinician.
      const key = `${m.drug_name.toLowerCase()}|${(m.dosage ?? '').toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(m);
    }
  }
  return result;
}

function computeDateRange(events: PublicShareEvent[]): string | null {
  const dates = events
    .map((e) => e.event_date)
    .filter((d): d is string => typeof d === 'string' && d.length > 0)
    .sort();
  if (dates.length === 0) return null;
  const first = dates[0].slice(0, 4);
  const last = dates[dates.length - 1].slice(0, 4);
  return first === last ? first : `${first}–${last}`;
}

function countByType(events: PublicShareEvent[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const e of events) {
    counts[e.event_type] = (counts[e.event_type] ?? 0) + 1;
  }
  return counts;
}

function groupByYear(events: PublicShareEvent[]): Array<[string, PublicShareEvent[]]> {
  const map = new Map<string, PublicShareEvent[]>();
  for (const e of events) {
    const year = e.event_date ? e.event_date.slice(0, 4) : 'Undated';
    const list = map.get(year);
    if (list) list.push(e);
    else map.set(year, [e]);
  }
  // Sort years newest-first; "Undated" goes last.
  return Array.from(map.entries()).sort(([a], [b]) => {
    if (a === 'Undated') return 1;
    if (b === 'Undated') return -1;
    return b.localeCompare(a);
  });
}
